import { useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Cog, MinusCircle, PlusCircle } from 'lucide-react'

import { MerkleStateChangeItem } from '~/components/txn-display/state-change/types'
import { useActiveOrg } from '~/hooks/org'
import {
  BackendTransaction,
  BasicTransactionType,
  MerkleTransactionUpdateNetworksType,
  OrgMerkleTransaction,
} from '~/types'
import { getChainLabel } from '~/utils/chainLabels'

export const useNetworkUpdates = ({
  transaction,
}: {
  transaction: BackendTransaction
}): MerkleStateChangeItem[] => {
  const { org: activeOrg, isLoading } = useActiveOrg()
  const queryClient = useQueryClient()

  return useMemo(() => {
    if (!transaction.types.includes(BasicTransactionType.AddNetworks)) return []

    const contents: MerkleStateChangeItem[] = []

    const updateNetworksTx = (
      transaction as OrgMerkleTransaction
    ).txData.transactions.find(
      (tx) => tx.type === MerkleTransactionUpdateNetworksType.UpdateNetworks,
    )
    const oldNetworks = activeOrg?.smartAccountsChainIds ?? []
    const newNetworks =
      updateNetworksTx && 'newChainIds' in updateNetworksTx
        ? updateNetworksTx.newChainIds
        : []

    const oldNetworkSet = new Set(oldNetworks)
    const newNetworkSet = new Set(newNetworks)

    // Use addedChainIds from tx data if available (older transactions won't have it)
    const addedNetworks =
      updateNetworksTx?.addedChainIds !== undefined
        ? updateNetworksTx.addedChainIds
        : newNetworks.filter((chainId) => !oldNetworkSet.has(chainId))
    const removedNetworks = oldNetworks.filter(
      (chainId) => !newNetworkSet.has(chainId),
    )

    const formatNetworks = (chainIds: number[]) =>
      chainIds
        .map((chainId) => getChainLabel(chainId, { queryClient }))
        .join(', ')

    if (addedNetworks.length) {
      contents.push({
        title: 'Add networks',
        icon: <PlusCircle />,
        description: formatNetworks(addedNetworks),
        isLoading,
      })
    }

    if (removedNetworks.length) {
      contents.push({
        title: 'Remove networks',
        icon: <MinusCircle />,
        description: formatNetworks(removedNetworks),
        isLoading,
      })
    }

    if (!addedNetworks.length && !removedNetworks.length) {
      contents.push({
        title: 'Update networks',
        icon: <Cog />,
        description: formatNetworks(newNetworks) || 'No network changes',
        isLoading,
      })
    }

    return contents
  }, [transaction, activeOrg?.smartAccountsChainIds, isLoading, queryClient])
}
